import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

const CONVERSION_DASH_PATTERNS = [0, 6, 1, 12]
const SVG_NAMESPACE = "http://www.w3.org/2000/svg"

// Charts for the admin application completion funnel dashboard.
//   variant "funnel"     — horizontal bars, one per stage, over the whole
//                          range; a stage carrying several segments (the
//                          completed stage, split into approved and denied)
//                          renders as one bar split into those segments
//   variant "cohorts"    — line chart of stage counts per cohort
//   variant "conversion" — line chart of the headline conversion rates per cohort
// All three read from the same payload the controller renders, so the three
// views can never disagree about the underlying numbers.
//
// With `selectable`, the chart renders its own legend as toggle chips: click a
// chip to show/hide that series, "only" to isolate it, "Show all" to reset. The
// selection is remembered per storage key so a reload keeps the chosen view.
// A chip's swatch repeats its series' line and dash pattern, so the series can
// be told apart without relying on hue alone.
export default class extends Controller {
  static targets = ["chart", "chips"]

  static values = {
    variant: String,
    stages: Array,
    cohorts: Array,
    series: Array,
    height: Number,
    selectable: Boolean,
    storageKey: String
  }

  connect() {
    this.hidden = this.restoreHidden()

    const options = this.optionsFor(this.variantValue)
    if (!options) return

    this.chart = new ApexCharts(this.chartElement, options)
    this.chart.render().then(() => this.applyHidden())
    this.renderChips()
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
    if (this.hasChipsTarget) this.chipsTarget.replaceChildren()
  }

  get chartElement() {
    return this.hasChartTarget ? this.chartTarget : this.element
  }

  get selectionEnabled() {
    return this.selectableValue && this.hasChipsTarget
  }

  get seriesNames() {
    return (this.seriesValue || []).map((s) => s.name)
  }

  get storageKey() {
    return `application-completion-funnel-chart:hidden:${this.storageKeyValue || this.variantValue}`
  }

  restoreHidden() {
    if (!this.selectableValue) return new Set()

    const names = this.seriesNames
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.storageKey) || "[]")
      const hidden = new Set(Array.isArray(stored) ? stored.filter((name) => names.includes(name)) : [])
      // Never restore a state with nothing plotted — that reads as a broken chart.
      return hidden.size >= names.length ? new Set() : hidden
    } catch {
      return new Set()
    }
  }

  persistHidden() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify([...this.hidden]))
    } catch {
      // Storage can be unavailable (private mode, quota) — the toggles still work.
    }
  }

  applyHidden() {
    if (!this.chart) return

    this.seriesNames.forEach((name) => {
      if (this.hidden.has(name)) {
        this.chart.hideSeries(name)
      } else {
        this.chart.showSeries(name)
      }
    })
  }

  setHidden(names) {
    this.hidden = new Set(names)
    this.persistHidden()
    this.applyHidden()
    this.renderChips()
  }

  toggleSeries(name) {
    const hidden = new Set(this.hidden)
    if (hidden.has(name)) {
      hidden.delete(name)
    } else if (hidden.size < this.seriesNames.length - 1) {
      // Keep at least one series plotted.
      hidden.add(name)
    }
    this.setHidden(hidden)
  }

  isolateSeries(name) {
    this.setHidden(this.seriesNames.filter((other) => other !== name))
  }

  renderChips() {
    if (!this.selectionEnabled) return

    const chips = this.seriesValue.map((s) => this.buildChip(s))
    if (this.hidden.size > 0) chips.push(this.buildShowAllChip())
    this.chipsTarget.replaceChildren(...chips)
  }

  buildChip(series) {
    const isHidden = this.hidden.has(series.name)

    const wrapper = document.createElement("span")
    wrapper.className = `inline-flex items-center rounded-full border text-xs ${
      isHidden ? "border-support-light-400 bg-white" : "border-support-light-500 bg-support-light-200"
    }`

    const toggle = document.createElement("button")
    toggle.type = "button"
    // The `!` utilities are the repo idiom for opting a <button> out of the
    // global purple pill styling.
    toggle.className = `flex items-center gap-1.5 !pl-2.5 !pr-2 !py-1 !rounded-l-full !bg-transparent !text-xs !font-semibold ${
      isHidden ? "!text-support-light-700" : "!text-support-dark-700"
    }`
    toggle.setAttribute("aria-pressed", String(!isHidden))
    toggle.title = isHidden ? `Show ${series.name}` : `Hide ${series.name}`
    toggle.addEventListener("click", () => this.toggleSeries(series.name))

    const swatch = this.buildChipSwatch(series, isHidden)

    const label = document.createElement("span")
    label.textContent = series.name
    if (isHidden) label.className = "line-through"

    toggle.append(swatch, label)

    const only = document.createElement("button")
    only.type = "button"
    only.textContent = "only"
    only.className =
      "!pl-2 !pr-2.5 !py-1 !rounded-r-full !bg-transparent border-l !border-support-light-400 !text-xs !font-semibold !text-support-light-700 hover:!text-core-purple-600"
    only.title = `Show only ${series.name}`
    only.addEventListener("click", () => this.isolateSeries(series.name))

    wrapper.append(toggle, only)
    return wrapper
  }

  // A line drawn with the same dash pattern the chart plots, so a dashed series
  // is identifiable from the legend alone. Other variants keep a plain dot.
  buildChipSwatch(series, isHidden) {
    const color = isHidden ? "rgb(var(--color-support-light-500))" : series.color

    if (this.variantValue !== "conversion") {
      const dot = document.createElement("span")
      dot.className = "w-2.5 h-2.5 rounded-full shrink-0"
      dot.style.backgroundColor = color
      return dot
    }

    const index = this.seriesNames.indexOf(series.name)
    const dash = CONVERSION_DASH_PATTERNS[index % CONVERSION_DASH_PATTERNS.length]

    const svg = document.createElementNS(SVG_NAMESPACE, "svg")
    svg.setAttribute("width", "20")
    svg.setAttribute("height", "8")
    svg.setAttribute("viewBox", "0 0 20 8")
    svg.setAttribute("aria-hidden", "true")
    svg.classList.add("shrink-0")

    const line = document.createElementNS(SVG_NAMESPACE, "line")
    line.setAttribute("x1", "0")
    line.setAttribute("y1", "4")
    line.setAttribute("x2", "20")
    line.setAttribute("y2", "4")
    line.setAttribute("stroke", color)
    line.setAttribute("stroke-width", "3")
    // Apex reads dashArray as the dash length with an equal gap; a 1 renders as
    // dots, which need round caps to stay visible at this size.
    if (dash > 0) {
      line.setAttribute("stroke-dasharray", `${dash} ${dash}`)
      line.setAttribute("stroke-linecap", dash === 1 ? "round" : "butt")
    }

    svg.append(line)
    return svg
  }

  buildShowAllChip() {
    const button = document.createElement("button")
    button.type = "button"
    button.textContent = "Show all"
    button.className =
      "!px-2.5 !py-1 !rounded-full border !border-core-purple-500 !bg-white !text-xs !font-semibold !text-core-purple-600 hover:!bg-core-purple-200"
    button.addEventListener("click", () => this.setHidden([]))
    return button
  }

  optionsFor(variant) {
    switch (variant) {
      case "funnel":
        return this.funnelOptions()
      case "cohorts":
        return this.cohortOptions()
      case "conversion":
        return this.conversionOptions()
      default:
        return null
    }
  }

  get baseChart() {
    return {
      height: this.heightValue || 320,
      toolbar: { show: false },
      fontFamily: "'Neue Haas Grotesk Text Pro','DM Sans',sans-serif",
      animations: { easing: "easeinout", speed: 500 }
    }
  }

  get gridDefaults() {
    return {
      borderColor: "#EEF0F5",
      strokeDashArray: 4,
      padding: { left: 8, right: 16 }
    }
  }

  // Bars are stacked so a split stage reads as one bar: series N holds the Nth
  // segment of every stage, and stages with fewer segments plot null there.
  funnelOptions() {
    const stages = this.stagesValue || []
    if (stages.length === 0) return null

    const segmentsDeep = Math.max(...stages.map((s) => (s.segments || []).length))
    const segmentAt = (stageIndex, segmentIndex) => (stages[stageIndex].segments || [])[segmentIndex]
    const depth = [...Array(segmentsDeep).keys()]

    return {
      chart: { ...this.baseChart, type: "bar", stacked: true },
      series: depth.map((segmentIndex) => ({
        name: segmentIndex === 0 ? "Members" : "Split",
        data: stages.map((_stage, stageIndex) => segmentAt(stageIndex, segmentIndex)?.count ?? null)
      })),
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          borderRadiusApplication: "end",
          barHeight: "62%",
          dataLabels: { position: "top", hideOverflowingLabels: false }
        }
      },
      colors: depth.map(
        (segmentIndex) => (opts) => segmentAt(opts.dataPointIndex, segmentIndex)?.color ?? "transparent"
      ),
      legend: { show: false },
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        offsetX: 8,
        style: { fontSize: "12px", fontWeight: 600, colors: ["#2D3047"] },
        formatter: (value, opts) => {
          const stage = stages[opts.dataPointIndex]
          const segment = segmentAt(opts.dataPointIndex, opts.seriesIndex)
          if (!segment) return ""

          // A split stage is labelled once, past the end of the whole bar, so
          // the segment labels cannot collide with each other.
          const isLastSegment = opts.seriesIndex === stage.segments.length - 1
          if (stage.segments.length > 1) {
            return isLastSegment ? `${stage.count.toLocaleString()}  ·  ${stage.share}% ${stage.baseLabel}` : ""
          }

          return segment.count ? `${value.toLocaleString()}  ·  ${stage.share}% ${stage.baseLabel}` : ""
        }
      },
      xaxis: {
        // Headroom past the longest bar so its label reads inside the plot.
        min: 0,
        max: Math.max(10, Math.ceil((Math.max(...stages.map((s) => s.count)) * 1.4) / 10) * 10),
        tickAmount: 5,
        categories: stages.map((s) => s.label),
        labels: { style: { colors: "#8B8FA3", fontSize: "11px" } },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: { style: { colors: "#5A5E78", fontSize: "12px", fontWeight: 600 } }
      },
      grid: this.gridDefaults,
      tooltip: { shared: true, intersect: false, custom: (opts) => this.funnelTooltip(stages, opts) }
    }
  }

  // Hand-rolled so a split stage lists every segment with its own count and
  // share, plus the stage total, rather than one row per stacked series.
  funnelTooltip(stages, { dataPointIndex }) {
    const stage = stages[dataPointIndex]
    if (!stage) return ""

    const rows = stage.segments
      .map(
        (segment) => `<div class="flex items-center gap-2">
          <span class="inline-block w-2 h-2 rounded-sm" style="background: ${segment.color}"></span>
          <span>${segment.label}: <strong>${segment.count.toLocaleString()}</strong> (${segment.share}% ${segment.baseLabel})</span>
        </div>`
      )
      .join("")
    const total =
      stage.segments.length > 1
        ? `<div class="mt-1 pt-1 border-t border-support-light-300">Total: <strong>${stage.count.toLocaleString()}</strong> (${stage.share}% ${stage.baseLabel})</div>`
        : ""

    return `<div class="p-2 text-xs text-support-dark-700">
      <div class="font-semibold text-support-dark-800 mb-1">${stage.label}</div>
      ${rows}${total}
    </div>`
  }

  cohortOptions() {
    const cohorts = this.cohortsValue || []
    const series = this.seriesValue || []
    if (cohorts.length === 0 || series.length === 0) return null

    return {
      chart: { ...this.baseChart, type: "line" },
      series: series.map((s) => ({ name: s.name, data: s.data })),
      colors: series.map((s) => s.color),
      dataLabels: { enabled: false },
      stroke: { width: 3, curve: "straight", lineCap: "butt" },
      markers: { size: 4, strokeWidth: 0, hover: { size: 6 } },
      xaxis: {
        categories: cohorts,
        labels: { style: { colors: "#8B8FA3", fontSize: "11px" }, rotate: -35, rotateAlways: cohorts.length > 8 },
        axisBorder: { color: "#EEF0F5" },
        axisTicks: { show: false }
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: { style: { colors: "#8B8FA3", fontSize: "11px" } }
      },
      legend: { position: "top", horizontalAlign: "left", fontSize: "12px", markers: { radius: 4 } },
      grid: this.gridDefaults,
      tooltip: { shared: true, intersect: false }
    }
  }

  conversionOptions() {
    const cohorts = this.cohortsValue || []
    const series = this.seriesValue || []
    if (cohorts.length === 0 || series.length === 0) return null

    return {
      chart: { ...this.baseChart, type: "line" },
      series: series.map((s) => ({ name: s.name, data: s.data })),
      colors: series.map((s) => s.color),
      stroke: {
        width: 3,
        curve: "straight",
        dashArray: CONVERSION_DASH_PATTERNS,
        lineCap: "butt"
      },
      markers: { size: 4, strokeWidth: 0, hover: { size: 6 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: cohorts,
        labels: { style: { colors: "#8B8FA3", fontSize: "11px" }, rotate: -35, rotateAlways: cohorts.length > 8 },
        axisBorder: { color: "#EEF0F5" },
        axisTicks: { show: false }
      },
      // No fixed maximum: a rate can legitimately exceed 100% when the two
      // stages are counted independently (applications by unscreened users).
      yaxis: {
        min: 0,
        tickAmount: 4,
        labels: {
          style: { colors: "#8B8FA3", fontSize: "11px" },
          formatter: (value) => `${Math.round(value)}%`
        }
      },
      // The chips take the legend's place when the series are selectable.
      legend: this.selectionEnabled
        ? { show: false }
        : { position: "top", horizontalAlign: "left", fontSize: "12px", markers: { radius: 4 } },
      grid: this.gridDefaults,
      tooltip: { shared: true, intersect: false, y: { formatter: (value) => (value === null ? "—" : `${value}%`) } }
    }
  }
}
